import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FormikErrors, FormikTouched } from 'formik';
import { IoMdMail } from 'react-icons/io';

import { isProduction } from '@/settings/environment';
import { CapyCaptcha, SetCapyValues } from '@/components/shared/CapyCaptcha';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';

import * as styles from './index.module.scss';

export type Values = {
  email: string;
  ignoreCapyCaptcha: boolean;
};

type Props = {
  errors: FormikErrors<Values>;
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T = string | React.ChangeEvent<any>>(
      field: T,
    ): T extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  touched: FormikTouched<Values>;
  values: Values;
  capyError: boolean;
  setCapyValues: SetCapyValues;
};
export const FormContents: React.FC<Props> = ({
  errors,
  handleChange,
  handleSubmit,
  isLoading,
  touched,
  values,
  capyError,
  setCapyValues,
}) => {
  const { t } = useTranslation();
  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="email">
          {t('ui.email.label')}
        </label>
        <div className={classnames(styles.inputWrapper, styles.iconLeft)}>
          <IoMdMail className={styles.icon} />
          <input
            id="email"
            data-test="email-field"
            className={classnames(styles.input, {
              [styles.error]: errors.email && touched.email,
            })}
            name="email"
            type="email"
            onChange={handleChange}
            placeholder={t('ui.email.placeholder')}
            value={values.email}
          />
        </div>
        {touched.email && errors.email && (
          <div>
            <Label>{errors.email}</Label>
          </div>
        )}
      </div>

      <div className={styles.capyWrapper}>
        <CapyCaptcha onChange={setCapyValues} />
        <div>{t('ui.capyCaptcha.description')}</div>
        {capyError && <Label>{t('ui.capyCaptcha.error')}</Label>}
        {!isProduction() && (
          <label className={styles.checkbox} htmlFor="ignoreCapyCaptcha">
            <input
              type="checkbox"
              id="ignoreCapyCaptcha"
              name="ignoreCapyCaptcha"
              data-test="ignore-capy-captcha"
              onChange={handleChange}
              checked={values.ignoreCapyCaptcha}
            />
            <span className={styles.checkboxBox}></span>
            <span className={styles.checkboxLabel}>
              {t('ui.capyCaptcha.ignore')}
            </span>
          </label>
        )}
      </div>
      <div className="submit-button">
        <SharedButton dataTest="do-verify" disabled={isLoading} type="submit">
          {t('ui.action.register')}
        </SharedButton>
      </div>
    </form>
  );
};
